'use client'

import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import type { LandingContent } from '@/types/landing'

const chipLayout = [
  { x: '2%', y: '16%', anchor: 'left', duration: '5.2s', delay: '0s' },
  { x: '30%', y: '6%', anchor: 'center', duration: '6.1s', delay: '.6s' },
  { x: '70%', y: '7%', anchor: 'center', duration: '5.6s', delay: '1.1s' },
  { x: '98%', y: '17%', anchor: 'right', duration: '6.4s', delay: '.3s' },
  { x: '1%', y: '36%', anchor: 'left', duration: '5.9s', delay: '.9s' },
  { x: '99%', y: '64%', anchor: 'right', duration: '5.4s', delay: '1.4s' },
  { x: '2%', y: '82%', anchor: 'left', duration: '6.2s', delay: '.2s' },
  { x: '34%', y: '93%', anchor: 'center', duration: '5.7s', delay: '1s' },
  { x: '68%', y: '93%', anchor: 'center', duration: '6s', delay: '.5s' },
  { x: '98%', y: '81%', anchor: 'right', duration: '5.5s', delay: '1.3s' },
] as const

type MemoryRay = { x: number; y: number; tone: 'red' | 'amber' | 'gold'; width: number; opacity: number; treatment: 'connected' | 'fade'; chipIndex?: number }

const memoryRays: MemoryRay[] = [
  { x: 150, y: 109, tone: 'red', width: .8, opacity: .74, treatment: 'connected', chipIndex: 0 },
  { x: 390, y: 41, tone: 'amber', width: 1.15, opacity: .86, treatment: 'connected', chipIndex: 1 },
  { x: 910, y: 48, tone: 'red', width: .95, opacity: .8, treatment: 'connected', chipIndex: 2 },
  { x: 1140, y: 116, tone: 'amber', width: .75, opacity: .68, treatment: 'connected', chipIndex: 3 },
  { x: 130, y: 245, tone: 'red', width: 1.3, opacity: .84, treatment: 'connected', chipIndex: 4 },
  { x: 1160, y: 435, tone: 'amber', width: .85, opacity: .7, treatment: 'connected', chipIndex: 5 },
  { x: 170, y: 558, tone: 'gold', width: .65, opacity: .58, treatment: 'connected', chipIndex: 6 },
  { x: 442, y: 632, tone: 'red', width: 1.05, opacity: .68, treatment: 'connected', chipIndex: 7 },
  { x: 884, y: 632, tone: 'amber', width: .75, opacity: .6, treatment: 'connected', chipIndex: 8 },
  { x: 1130, y: 551, tone: 'red', width: 1.2, opacity: .66, treatment: 'connected', chipIndex: 9 },
  { x: 72, y: 78, tone: 'gold', width: .55, opacity: .62, treatment: 'fade' },
  { x: 260, y: 62, tone: 'amber', width: .45, opacity: .58, treatment: 'fade' },
  { x: 510, y: 96, tone: 'gold', width: .6, opacity: .62, treatment: 'fade' },
  { x: 790, y: 90, tone: 'amber', width: .5, opacity: .56, treatment: 'fade' },
  { x: 1040, y: 66, tone: 'gold', width: .45, opacity: .6, treatment: 'fade' },
  { x: 1238, y: 82, tone: 'red', width: .5, opacity: .54, treatment: 'fade' },
  { x: 280, y: 390, tone: 'gold', width: .55, opacity: .62, treatment: 'fade' },
  { x: 1020, y: 402, tone: 'red', width: .7, opacity: .66, treatment: 'fade' },
  { x: 318, y: 616, tone: 'amber', width: .45, opacity: .5, treatment: 'fade' },
  { x: 558, y: 660, tone: 'gold', width: .5, opacity: .48, treatment: 'fade' },
  { x: 742, y: 660, tone: 'red', width: .5, opacity: .46, treatment: 'fade' },
  { x: 982, y: 616, tone: 'gold', width: .45, opacity: .48, treatment: 'fade' },
]

const impellerPositions = ['6%', '35%', '65%', '94%'] as const
const impellerOffsets = ['-18%', '-154%', '-290%', '-426%'] as const

const svgThumb = (body: string) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 42 54">${body}</svg>`
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`
}
const G = '#9a9a9a', GL = '#d4d4d4', R2 = '#B80000', K = '#1D1D1D'
const lines = (x: number, y: number, w: number, n: number, gap = 4, color = GL) => Array.from({ length: n }, (_, index) =>
  `<rect x="${x}" y="${y + index * gap}" width="${w - (index % 3) * 4}" height="1.6" fill="${color}"/>`).join('')
const thumbs: Record<string, string> = {
  pdf: svgThumb(`<rect width="42" height="54" fill="#fff"/><rect width="42" height="9" fill="${R2}"/><text x="3" y="6.8" font-family="Arial" font-size="5" fill="#fff" font-weight="bold">PDF</text>${lines(4, 15, 32, 7)}<rect x="4" y="44" width="16" height="5" fill="${GL}"/>`),
  xls: svgThumb(`<rect width="42" height="54" fill="#fff"/><rect width="42" height="8" fill="#1a6e3c"/>${[0,1,2,3,4,5].map(row => `<rect x="3" y="${11 + row * 7}" width="36" height="6" fill="${row % 2 ? '#f0f0f0' : '#fafafa'}"/>`).join('')}<line x1="14" y1="11" x2="14" y2="52" stroke="${GL}" stroke-width=".8"/><line x1="26" y1="11" x2="26" y2="52" stroke="${GL}" stroke-width=".8"/>${lines(4, 13.5, 8, 6, 7, G)}`),
  cad: svgThumb(`<rect width="42" height="54" fill="#20242a"/><g stroke="#7fd4ff" stroke-width=".9" fill="none"><path d="M12 20 L28 14 L34 22 L18 28 Z"/><path d="M12 20 L12 34 L18 42 L18 28"/><path d="M34 22 L34 36 L18 42"/><path d="M28 14 L28 28 L34 36" stroke-dasharray="2 1.5"/></g><text x="3" y="51" font-family="Arial" font-size="4.5" fill="#7fd4ff">.STEP</text>`),
  dwg: svgThumb(`<rect width="42" height="54" fill="#fff"/><rect x="2" y="2" width="38" height="50" fill="none" stroke="${G}" stroke-width=".8"/><circle cx="21" cy="22" r="9" fill="none" stroke="${K}" stroke-width="1"/><circle cx="21" cy="22" r="3.5" fill="none" stroke="${K}" stroke-width=".8"/><line x1="8" y1="22" x2="34" y2="22" stroke="${G}" stroke-width=".5" stroke-dasharray="2 1.5"/><line x1="21" y1="9" x2="21" y2="35" stroke="${G}" stroke-width=".5" stroke-dasharray="2 1.5"/><rect x="4" y="40" width="34" height="10" fill="none" stroke="${G}" stroke-width=".6"/><line x1="4" y1="45" x2="38" y2="45" stroke="${G}" stroke-width=".5"/>${lines(6, 42.5, 12, 1, 4, G)}`),
  email: svgThumb(`<rect width="42" height="54" fill="#fff"/><rect x="0" y="0" width="42" height="12" fill="#f0f0f0"/><circle cx="7" cy="6" r="3" fill="${GL}"/>${lines(13, 4, 22, 2, 4, G)}${lines(4, 17, 34, 4)}<rect x="0" y="33" width="42" height="1" fill="${GL}"/>${lines(4, 38, 34, 4)}`),
  mtr: svgThumb(`<rect width="42" height="54" fill="#fffdf5"/><rect x="3" y="3" width="36" height="7" fill="none" stroke="${G}" stroke-width=".7"/><text x="6" y="8.5" font-family="Arial" font-size="4.5" fill="${K}">CERT</text>${lines(4, 15, 34, 6)}<circle cx="32" cy="44" r="6" fill="none" stroke="${R2}" stroke-width="1"/><path d="M29.5 44 l1.8 1.8 l3.2 -3.6" stroke="${R2}" stroke-width="1" fill="none"/>`),
  act: svgThumb(`<rect width="42" height="54" fill="#fff"/><circle cx="10" cy="9" r="4" fill="${GL}"/>${lines(17, 6, 18, 2, 4, G)}<rect x="4" y="18" width="34" height="12" rx="2" fill="#f5f5f5"/>${lines(7, 21.5, 26, 2, 4)}<rect x="4" y="34" width="20" height="7" rx="3.5" fill="${R2}"/><text x="7" y="39" font-family="Arial" font-size="4" fill="#fff">APPROVED</text>`),
  dec: svgThumb(`<rect width="42" height="54" fill="#fff"/><path d="M21 6 L21 18 M21 18 L10 28 M21 18 L32 28" stroke="${G}" stroke-width="1" fill="none"/><circle cx="21" cy="6" r="3" fill="${K}"/><rect x="5" y="28" width="10" height="8" rx="1.5" fill="none" stroke="${R2}" stroke-width="1"/><rect x="27" y="28" width="10" height="8" rx="1.5" fill="none" stroke="${GL}" stroke-width="1"/><path d="M8 32 l1.5 1.5 l2.8 -3" stroke="${R2}" stroke-width="1" fill="none"/>${lines(5, 42, 32, 3)}`),
  erp: svgThumb(`<rect width="42" height="54" fill="#fff"/><rect width="42" height="7" fill="${K}"/>${[0,1,2,3,4,5,6].map(row => `<line x1="0" y1="${13.5 + row * 6}" x2="42" y2="${13.5 + row * 6}" stroke="${GL}" stroke-width=".6"/>`).join('')}${lines(3, 9.5, 14, 7, 6, G)}${lines(24, 9.5, 14, 7, 6, GL)}`),
  vendor: svgThumb(`<rect width="42" height="54" fill="#fff"/><rect width="42" height="9" fill="${K}"/><text x="3" y="6.8" font-family="Arial" font-size="5" fill="#fff" font-weight="bold">QUOTE</text>${lines(4, 15, 32, 4)}<rect x="4" y="34" width="34" height="1" fill="${GL}"/><text x="4" y="45" font-family="Arial" font-size="6" fill="${K}" font-weight="bold">$8,240</text>`),
}

function SearchIcon() {
  return <svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="7" cy="7" r="4.5" /><line x1="10.5" y1="10.5" x2="14" y2="14" /></svg>
}

function FrankAnswer({ parts }: { parts: { text: string; emphasis?: boolean }[] }) {
  return <div className="fp-ask-answer"><span aria-hidden="true">F</span><p>{parts.map((part, index) => part.emphasis ? <strong key={index}>{part.text}</strong> : part.text)}</p></div>
}

function PriceChart({ label }: { label: string }) {
  const scaleY = (value: number) => 250 - (value - 2.8) * 220 / .7
  const prices = [2.84, 2.88, 2.86, 2.97, 3.17, 3.35, 3.24, 3.29, 3.35]
  const points = prices.map((price, index) => [50 + index * 36.75, scaleY(price)])
  return <svg className="fp-price-chart" viewBox="0 0 360 300" aria-label="A276 cost per pound rose from July 2024 to July 2026">
    <line x1="36" y1="30" x2="36" y2="250" /><line x1="36" y1="250" x2="350" y2="250" />
    <line className="grid" x1="36" y1="140" x2="350" y2="140" />
    <text x="30" y="34" textAnchor="end">$3.50</text><text x="30" y="144" textAnchor="end">$3.15</text><text x="30" y="253" textAnchor="end">$2.80</text>
    <polyline points={points.map(point => point.join(',')).join(' ')} />
    {points.map(([cx,cy], index) => <circle className={index === points.length - 1 ? 'last' : ''} cx={cx} cy={cy} r="3.5" key={cx} />)}
    <text className="value" x="344" y={points.at(-1)![1] - 16} textAnchor="end">{label}</text>
    <text x="50" y="280">Jul &apos;24</text><text x="196" y="280" textAnchor="middle">Jul &apos;25</text><text x="344" y="280" textAnchor="end">Jul &apos;26</text>
  </svg>
}

export default function ShopMemory({ content }: { content: LandingContent['memory'] }) {
  const stageRef = useRef<HTMLDivElement>(null)
  const askRef = useRef<HTMLDivElement>(null)
  const askCarouselRef = useRef<HTMLDivElement>(null)
  const [showAllMemories, setShowAllMemories] = useState(false)
  const [activeAnswer, setActiveAnswer] = useState(0)

  useEffect(() => {
    const stage = stageRef.current
    if (!stage || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    let frame = 0
    const update = () => {
      frame = 0
      if (window.innerWidth <= 1050) { stage.removeAttribute('data-scroll-expanded'); return }
      const rect = stage.getBoundingClientRect()
      const overallProgress = Math.max(0, Math.min(1, (window.innerHeight - rect.top) / (window.innerHeight * .68)))
      stage.dataset.scrollExpanded = 'true'
      stage.querySelectorAll<HTMLElement>('.fp-memory-chip').forEach((chip, index) => {
        const layout = chipLayout[index]
        const x = Number.parseFloat(layout.x)
        const y = Number.parseFloat(layout.y)
        const itemDelay = y > 75 ? .1 : y > 50 ? .05 : 0
        const progress = Math.max(0, Math.min(1, (overallProgress - itemDelay) / (1 - itemDelay)))
        const fanProgress = Math.min(1, progress * 1.35)
        chip.style.setProperty('--chip-dx', `${(50 - x) * rect.width / 100 * (1 - fanProgress)}px`)
        chip.style.setProperty('--chip-dy', `${(20 - y * rect.height / 100) * (1 - progress)}px`)
        chip.style.setProperty('--chip-scale', `${.72 + progress * .28}`)
        // Keep text at full visual opacity while the cards fan outward. Partial
        // parent opacity blends the labels into the page and fails contrast
        // mid-transition even though the settled card is fully legible.
        chip.style.setProperty('--chip-opacity', '1')
      })
    }
    const requestUpdate = () => { if (!frame) frame = requestAnimationFrame(update) }
    update()
    window.addEventListener('scroll', requestUpdate, { passive: true })
    window.addEventListener('resize', requestUpdate)
    return () => { cancelAnimationFrame(frame); window.removeEventListener('scroll', requestUpdate); window.removeEventListener('resize', requestUpdate) }
  }, [])

  const syncActiveAnswer = () => {
    const carousel = askCarouselRef.current
    if (!carousel) return
    const center = carousel.scrollLeft + carousel.clientWidth / 2
    const cards = Array.from(carousel.querySelectorAll<HTMLElement>('.fp-ask-card'))
    const closest = cards.reduce((best, card, index) => {
      const distance = Math.abs(card.offsetLeft + card.offsetWidth / 2 - center)
      return distance < best.distance ? { index, distance } : best
    }, { index: 0, distance: Number.POSITIVE_INFINITY })
    setActiveAnswer(closest.index)
  }

  const showAnswer = (index: number) => {
    const carousel = askCarouselRef.current
    const card = carousel?.querySelectorAll<HTMLElement>('.fp-ask-card')[index]
    if (!carousel || !card) return
    carousel.scrollTo({
      left: card.offsetLeft - (carousel.clientWidth - card.offsetWidth) / 2,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    })
    setActiveAnswer(index)
  }

  useEffect(() => {
    const ask = askRef.current
    if (!ask) return
    if (!('IntersectionObserver' in window)) { ask.dataset.askVisible = 'true'; return }
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return
      ask.dataset.askVisible = 'true'
      observer.disconnect()
    }, { threshold: .18 })
    observer.observe(ask)
    return () => observer.disconnect()
  }, [])

  return <section className="fp-memory" aria-labelledby="fp-memory-title">
    <div className="fp-memory-stage" ref={stageRef}>
      <svg className="fp-memory-lines" viewBox="0 -170 1300 850" preserveAspectRatio="none" aria-hidden="true">
        <defs>
          <linearGradient id="fp-ray-connected-red" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#B80000" stopOpacity=".48" /><stop offset=".7" stopColor="#B80000" stopOpacity=".28" /><stop offset="1" stopColor="#B80000" stopOpacity=".18" /></linearGradient>
          <linearGradient id="fp-ray-connected-amber" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#D58A38" stopOpacity=".42" /><stop offset=".7" stopColor="#D58A38" stopOpacity=".24" /><stop offset="1" stopColor="#D58A38" stopOpacity=".15" /></linearGradient>
          <linearGradient id="fp-ray-connected-gold" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#E2B65A" stopOpacity=".36" /><stop offset=".7" stopColor="#E2B65A" stopOpacity=".2" /><stop offset="1" stopColor="#E2B65A" stopOpacity=".12" /></linearGradient>
          <linearGradient id="fp-ray-fade-red" data-ray-gradient="fade" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#B80000" stopOpacity=".42" /><stop offset=".68" stopColor="#B80000" stopOpacity=".2" /><stop offset=".88" stopColor="#B80000" stopOpacity=".06" /><stop offset="1" stopColor="#B80000" stopOpacity="0" /></linearGradient>
          <linearGradient id="fp-ray-fade-amber" data-ray-gradient="fade" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#D58A38" stopOpacity=".38" /><stop offset=".68" stopColor="#D58A38" stopOpacity=".18" /><stop offset=".88" stopColor="#D58A38" stopOpacity=".05" /><stop offset="1" stopColor="#D58A38" stopOpacity="0" /></linearGradient>
          <linearGradient id="fp-ray-fade-gold" data-ray-gradient="fade" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#E2B65A" stopOpacity=".32" /><stop offset=".68" stopColor="#E2B65A" stopOpacity=".15" /><stop offset=".88" stopColor="#E2B65A" stopOpacity=".04" /><stop offset="1" stopColor="#E2B65A" stopOpacity="0" /></linearGradient>
        </defs>
        {memoryRays.map((ray, index) => <line key={index} x1="650" y1="-160" x2={ray.x} y2={ray.y} stroke={`url(#fp-ray-${ray.treatment}-${ray.tone})`} strokeWidth={ray.width} opacity={ray.opacity} data-ray-treatment={ray.treatment} data-chip-index={ray.chipIndex} />)}
      </svg>
      <div className="fp-memory-copy"><h2 id="fp-memory-title">{content.heading}</h2><p>{content.description}</p></div>
      <div className="fp-memory-chips" data-expanded={showAllMemories || undefined} aria-hidden="true">
        {content.chips.map((chip, index) => {
          const layout = chipLayout[index]
          return <div className={`fp-memory-chip fp-memory-chip--${layout.anchor}`} key={chip.title} style={{ '--chip-x': layout.x, '--chip-y': layout.y, '--chip-duration': layout.duration, '--chip-delay': layout.delay } as React.CSSProperties}>
            <div className="fp-memory-chip-inner"><span className="fp-memory-thumbnail" style={{ backgroundImage: thumbs[chip.kind] }} aria-hidden="true" /><span><strong>{chip.title}</strong><small>{chip.subtitle}</small></span></div>
          </div>
        })}
      </div>
      <button
        className="fp-memory-toggle"
        type="button"
        aria-expanded={showAllMemories}
        onClick={() => setShowAllMemories((value) => !value)}
      >
        {showAllMemories ? 'Show fewer memories' : `See all ${content.chips.length} memories`}
        <span aria-hidden="true">{showAllMemories ? '↑' : '↓'}</span>
      </button>
    </div>
    <div className="fp-ask" ref={askRef}>
      <div className="fp-ask-heading"><h3>{content.ask.heading}</h3></div>
      <div
        ref={askCarouselRef}
        className="fp-ask-grid"
        role="region"
        aria-roledescription="carousel"
        aria-label="Ask Frank examples"
        tabIndex={0}
        onScroll={syncActiveAnswer}
      >
        {content.ask.cards.map((card, cardIndex) => <article
          className="fp-ask-card"
          id={`fp-ask-example-${cardIndex + 1}`}
          aria-label={`Example ${cardIndex + 1} of ${content.ask.cards.length}: ${card.query}`}
          aria-roledescription="slide"
          key={card.query}
        >
          <div className="fp-ask-search"><div><SearchIcon /><span>{card.query}</span>{'file' in card && <small>{card.file}</small>}</div></div>
          <div className="fp-ask-body">
            <FrankAnswer parts={card.answer} />
            {card.results && <div className="fp-part-grid">{card.results.map(result => <div className="fp-part-result" key={result.part}><div className="fp-impeller-crop" style={{ '--impeller-position': impellerPositions[result.variant - 1], '--impeller-left': impellerOffsets[result.variant - 1] } as React.CSSProperties}><Image className="fp-impeller-photo" src="/images/impeller-reference.png" alt={`Machined stainless steel impeller ${result.part}`} width={1796} height={375} sizes="(max-width: 700px) 900px, 800px" /></div><p><strong>{result.part}</strong><span className={`fp-result-${result.tone}`}>{result.detail}</span></p></div>)}</div>}
            {card.jobs && <div className="fp-job-list">{card.jobs.map(job => <div key={job.name}><strong>{job.name}</strong><span>{job.date}</span><span>{job.value}</span></div>)}<div className="fp-job-more"><strong>{card.more}</strong><span>{card.range}</span></div></div>}
            {card.chartLabel && <PriceChart label={card.chartLabel} />}
          </div>
        </article>)}
      </div>
      <div className="fp-ask-pagination" aria-label="Choose an Ask Frank example">
        {content.ask.cards.map((card, index) => <button
          type="button"
          aria-controls={`fp-ask-example-${index + 1}`}
          aria-current={activeAnswer === index ? 'true' : undefined}
          aria-label={`Show example ${index + 1}: ${card.query}`}
          onClick={() => showAnswer(index)}
          key={card.query}
        ><span /></button>)}
      </div>
    </div>
  </section>
}
